import React from 'react';
import { Edit2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import {
  getInitials,
  getRoleLabel,
  formatLongDate,
  resolvePlan,
} from '../../../features/user/userUtils';

const Field = ({ label, children }) => (
  <div>
    <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">
      {label}
    </div>
    <div className="text-sm font-semibold text-ninja-dark dark:text-white">{children}</div>
  </div>
);

/**
 * Who you are, as the backend has it.
 *
 * Everything here is derived from `get_user/` and the session. The version this
 * replaces had the name, the RUT, the email, the plan and the join date typed
 * into the markup, so every account saw "STAFF NinjaHubs" with RUT
 * 77.777.777-6.
 *
 * The fields shown adapt to what arrived, which is what lets one page serve the
 * five account types: an admin carries a RUT, a seller adds its company, an
 * operator its warehouse. Nothing is rendered for a field the profile does not
 * have, rather than an empty row or a dash.
 *
 * @param {object} props
 * @param {object} props.user - The `get_user/` payload.
 * @param {object} props.sessionUser - `state.auth.user`, for the plan.
 * @param {() => void} props.onEdit
 */
export const ProfileCard = ({ user, sessionUser, onEdit }) => {
  const role = getRoleLabel(user);
  const plan = resolvePlan(sessionUser);

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 shadow-sm border border-border">
      <div className="flex items-start gap-6 mb-8">
        <div className="w-20 h-20 rounded-full bg-ninja-greenLight text-ninja-green flex items-center justify-center text-2xl font-bold flex-shrink-0">
          {getInitials(user)}
        </div>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-ninja-dark dark:text-white mb-1 break-words">
            {user.full_name || user.email}
          </h2>
          {role && <p className="text-sm text-text-secondary mb-2">{role}</p>}

          {user.is_active ? (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ninja-greenLight text-ninja-green text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-ninja-green" />
              Activo
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 text-danger text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-danger" />
              Inactivo
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12 mb-8">
        <Field label="Nombre">{user.full_name || '-'}</Field>

        {user.rut && <Field label="RUT">{user.rut}</Field>}

        <Field label="Usuario">{user.username || user.email}</Field>
        <Field label="Correo">{user.email}</Field>

        {user.company_name && <Field label="Empresa">{user.company_name}</Field>}
        {user.warehouse_name && <Field label="Bodega">{user.warehouse_name}</Field>}
        {user.phone && <Field label="Teléfono">{user.phone}</Field>}

        <Field label="Plan">
          {plan ? (
            <>
              {plan.name} <span className="text-text-secondary font-normal">({plan.limit})</span>
            </>
          ) : (
            <span className="text-text-secondary font-normal">Sin plan asignado</span>
          )}
        </Field>

        <Field label="Fecha de registro">{formatLongDate(user.date_joined)}</Field>
      </div>

      <Button variant="ghost" icon={Edit2} onClick={onEdit}>
        Editar Perfil
      </Button>
    </div>
  );
};
